/**
 * Map classic e-Source AQ Assist fields → Form Studio AI JSON (sections/items).
 */
const WIDTH_TO_SPAN = {
  full: 12,
  half: 6,
  third: 4,
  quarter: 3,
};

const TYPE_MAP = {
  text: "short",
  textarea: "long",
  integer: "number",
  number: "number",
  calculated: "calc",
  dropdown: "dropdown",
  radio: "radio",
  multi_select: "checkboxes",
  checkbox: "consent",
  toggle: "yesno",
  button_group: "yesno",
  yesno: "yesno",
  date: "date",
  datetime: "date",
  time: "time",
  heading: "text",
  instruction: "text",
  signature: "signature",
  file: "file",
  rating: "rating",
  slider: "slider",
};

function optsOf(field) {
  const raw = field?.options ?? field?.Options ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) =>
      typeof o === "string"
        ? o
        : String(o?.label ?? o?.Label ?? o?.value ?? o?.Value ?? "").trim(),
    )
    .filter(Boolean);
}

function spanOf(field) {
  const w = String(field?.width ?? field?.Width ?? "full").toLowerCase();
  return WIDTH_TO_SPAN[w] || 12;
}

function fieldToItem(field) {
  const type = String(field?.type ?? field?.Type ?? "text").trim();
  if (type === "section" || type === "divider" || type === "spacer" || type === "pagebreak") {
    return null;
  }
  const label = String(field?.label ?? field?.Label ?? "").trim() || type || "Field";
  let mapped = TYPE_MAP[type] || "short";
  // Classic builder uses text for email/phone; Form Studio has dedicated types.
  if (
    mapped === "short" &&
    /e-?mail|email\s*id|emailid|mail\s*id/i.test(label)
  ) {
    mapped = "email";
  } else if (
    mapped === "short" &&
    /\b(phone|mobile|tel|contact\s*number)\b/i.test(label)
  ) {
    mapped = "phone";
  }
  const item = {
    type: mapped,
    label,
    key: String(field?.name ?? field?.Name ?? field?.key ?? "").trim() || undefined,
    required: !!(field?.required ?? field?.Required),
    span: spanOf(field),
  };
  if (mapped === "text" || type === "heading" || type === "instruction") {
    item.text = label;
    item.type = "text";
  }
  const options = optsOf(field);
  if (options.length && ["dropdown", "radio", "checkboxes"].includes(mapped)) {
    item.options = options;
  }
  if (mapped === "calc" && (field?.formula || field?.Formula)) {
    item.formula = String(field.formula || field.Formula);
  }
  if (field?.placeholder || field?.Placeholder) {
    item.placeholder = String(field.placeholder || field.Placeholder);
  }
  return item;
}

/**
 * @param {object[]} fields classic e-Source canvas fields
 * @param {{ title?: string, description?: string, summary?: string }} meta
 * @returns {{ title: string, description: string, sections: object[] }}
 */
export function mapEsourceFieldsToFormStudioSpec(fields = [], meta = {}) {
  const list = Array.isArray(fields) ? fields : [];
  const sections = [];
  let current = {
    title: "Form fields",
    items: [],
  };

  const pushCurrent = () => {
    if (current.items.length) {
      sections.push(current);
    }
    current = { title: "More fields", items: [] };
  };

  list.forEach((field) => {
    const type = String(field?.type ?? field?.Type ?? "").trim();
    if (type === "section") {
      pushCurrent();
      current = {
        title: String(field?.label ?? field?.Label ?? "Section").trim() || "Section",
        items: [],
      };
      return;
    }
    if (type === "divider" || type === "spacer" || type === "pagebreak") {
      return;
    }
    const item = fieldToItem(field);
    if (item) current.items.push(item);
  });
  pushCurrent();

  if (!sections.length) {
    sections.push({ title: "Form fields", items: [] });
  }

  return {
    title: String(meta.title || meta.name || "Untitled form").trim() || "Untitled form",
    description: String(meta.description || meta.summary || "").trim(),
    sections,
  };
}

function normalizeStudioFieldKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * Classic Assist often returns the full canvas after an incremental edit.
 * Form Studio only supports append — so pass only newly added fields.
 *
 * @param {object} assistResult
 * @param {object[]} [currentFields] canvas snapshot before this Assist turn
 * @returns {object[]}
 */
export function pickFieldsToAddOnStudio(assistResult, currentFields = []) {
  const all = Array.isArray(assistResult?.fields) ? assistResult.fields : [];
  if (!all.length) return [];

  const current = Array.isArray(currentFields) ? currentFields : [];
  const beforeIds = new Set(current.map((f) => f?.id).filter(Boolean));
  const beforeNames = new Set(
    current
      .map((f) =>
        normalizeStudioFieldKey(f?.fieldName || f?.name || f?.Name || ""),
      )
      .filter(Boolean),
  );
  const beforeLabels = new Set(
    current
      .map((f) => normalizeStudioFieldKey(f?.label || f?.Label || ""))
      .filter(Boolean),
  );

  // Prefer id delta (ops keep existing ids; new fields get new ids).
  if (beforeIds.size) {
    const byId = all.filter((f) => f?.id && !beforeIds.has(f.id));
    if (byId.length) return byId;
  }

  // Name / label delta when ids were regenerated or snapshot had none.
  if (current.length) {
    const byIdentity = all.filter((f) => {
      const name = normalizeStudioFieldKey(
        f?.fieldName || f?.name || f?.Name || "",
      );
      const label = normalizeStudioFieldKey(f?.label || f?.Label || "");
      if (name && beforeNames.has(name)) return false;
      if (label && beforeLabels.has(label)) return false;
      return true;
    });
    if (byIdentity.length && byIdentity.length < all.length) {
      return byIdentity;
    }
    // Incremental edit with no net adds (rename/remove/required only).
    if (assistResult?.incremental) return [];
  }

  // Empty canvas, or Assist returned only the new fields.
  return all;
}

function haystack(text, meta = {}) {
  return [
    String(text || ""),
    String(meta.fileName || ""),
    String(meta.title || meta.name || ""),
  ].join("\n");
}

/** Detect Panthera / CP05A-style IMP Accountability Log uploads. */
export function looksLikeImpAccountabilityLog(text, meta = {}) {
  const t = haystack(text, meta);
  return (
    /CP05A/i.test(t) ||
    /IMP\s+Accountability\s+Log/i.test(t) ||
    (/IP\s+Received\s+at\s+Site/i.test(t) &&
      /IP\s+Dispensed\s+to\s+Subject/i.test(t) &&
      /IP\s+Returned/i.test(t))
  );
}

/** SM02A Temperature Monitoring Log */
export function looksLikeTemperatureMonitoringLog(text, meta = {}) {
  const t = haystack(text, meta);
  return (
    /SM02A/i.test(t) ||
    /Temperature\s+Monitoring\s+Log/i.test(t) ||
    (/Make\s*\/?\s*Serial\s+Number/i.test(t) &&
      /Min\s*\(?\s*o?\s*C/i.test(t) &&
      /Actual\s*\(?\s*o?\s*C/i.test(t))
  );
}

/** CP03A Biological Sample Processing Log */
export function looksLikeBiologicalSampleProcessingLog(text, meta = {}) {
  const t = haystack(text, meta);
  return (
    /CP03A/i.test(t) ||
    /Biological\s+Sample\s+Processing\s+Log/i.test(t) ||
    (/Processing\s+Instructions/i.test(t) &&
      /Time\s+into\s+centrifuge/i.test(t) &&
      /Tube/i.test(t))
  );
}

/** CP07B Monthly Study Specific IMP Compliance Check */
export function looksLikeImpComplianceCheck(text, meta = {}) {
  const t = haystack(text, meta);
  return (
    /CP07B/i.test(t) ||
    /Monthly\s+Study\s+Specific\s+IMP\s+Compliance/i.test(t) ||
    (/Safety\s+Checks/i.test(t) &&
      /Accountability\s+Checks/i.test(t) &&
      /quarantined\s+stock/i.test(t))
  );
}

/**
 * Form Studio layout matching the uploaded IMP Accountability Log
 * (study header fields + one grouped multi-column datatable).
 */
export function buildImpAccountabilityLogStudioReply(meta = {}) {
  const title =
    String(meta.title || meta.name || "IMP Accountability Log").trim() ||
    "IMP Accountability Log";
  const received = "IP Received at Site";
  const dispensed =
    "IP Dispensed to Subject (do not dispense expired IP)";
  const returned =
    "IP Returned by Participant / IP Returned to Vendor/Destroyed";

  return {
    title,
    replace: true,
    applied: true,
    accent: "#6b2d7b",
    summary:
      "Rebuilt the IMP Accountability Log with study details and a grouped accountability table (Received / Dispensed / Returned). Review the canvas before saving.",
    sections: [
      {
        title: "Study details",
        items: [
          {
            type: "short",
            label: "Investigator Name",
            key: "investigator_name",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Site No",
            key: "site_no",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Sponsor Protocol No",
            key: "sponsor_protocol_no",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Internal Study Code",
            key: "internal_study_code",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Drug name",
            key: "drug_name",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Storage Location",
            key: "storage_location",
            required: true,
            span: 6,
          },
        ],
      },
      {
        title: "Accountability log",
        items: [
          {
            type: "datatable",
            label: "IMP Accountability Log",
            key: "imp_accountability_log",
            required: true,
            span: 12,
            rowMode: "repeat",
            rows: 10,
            columns: [
              {
                label: "Date Received (DD/MMM/YY)",
                key: "date_received",
                type: "date",
                group: received,
              },
              {
                label: "Shipment number",
                key: "shipment_number",
                type: "text",
                group: received,
              },
              {
                label: "Kit No.",
                key: "kit_no",
                type: "text",
                group: received,
              },
              {
                label: "Received by (Initials)",
                key: "received_by",
                type: "text",
                group: received,
              },
              {
                label: "Participant No.",
                key: "participant_no",
                type: "text",
                group: dispensed,
              },
              {
                label: "Date Dispensed (DD/MMM/YY)",
                key: "date_dispensed",
                type: "date",
                group: dispensed,
              },
              {
                label: "Dispensed by (Initials & date)",
                key: "dispensed_by",
                type: "text",
                group: dispensed,
              },
              {
                label: "Checked by (Initials & date)",
                key: "checked_by",
                type: "text",
                group: dispensed,
              },
              {
                label: "Date returned by Participant",
                key: "date_returned",
                type: "date",
                group: returned,
              },
              {
                label: "Amount of IMP returned",
                key: "amount_returned",
                type: "text",
                group: returned,
              },
              {
                label: "Completed by (Initials & date)",
                key: "completed_by",
                type: "text",
                group: returned,
              },
              {
                label: "Verified by CRA (Initials & date)",
                key: "verified_by_cra",
                type: "text",
                group: returned,
              },
              {
                label: "Returned/destroyed by (Initials & date)",
                key: "returned_destroyed_by",
                type: "text",
                group: returned,
              },
            ],
          },
          {
            type: "long",
            label: "Comments",
            key: "comments",
            span: 12,
          },
          {
            type: "text",
            text: "This document is confidential.",
            label: "Confidential",
            span: 12,
          },
        ],
      },
    ],
  };
}

/**
 * SM02A — Temperature Monitoring Log (header + temperature readings table).
 */
export function buildTemperatureMonitoringLogStudioReply(meta = {}) {
  const title =
    String(meta.title || meta.name || "Temperature Monitoring Log").trim() ||
    "Temperature Monitoring Log";
  return {
    title,
    replace: true,
    applied: true,
    accent: "#0d6efd",
    summary:
      "Rebuilt the Temperature Monitoring Log (SM02A) with site/equipment details and a readings table (Date, Time, Min/Max/Actual °C, Comments, Staff signature). Review before saving.",
    sections: [
      {
        title: "Location and equipment",
        items: [
          { type: "short", label: "Site", key: "site", required: true, span: 6 },
          { type: "short", label: "Location", key: "location", required: true, span: 6 },
          {
            type: "short",
            label: "Equipment",
            key: "equipment",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Make / Serial Number",
            key: "make_serial_number",
            required: true,
            span: 6,
          },
        ],
      },
      {
        title: "Temperature readings",
        items: [
          {
            type: "datatable",
            label: "Temperature Monitoring Log",
            key: "temperature_monitoring_log",
            required: true,
            span: 12,
            rowMode: "repeat",
            rows: 12,
            columns: [
              { label: "Date", key: "reading_date", type: "date" },
              { label: "Time", key: "reading_time", type: "time" },
              { label: "Min (°C)", key: "min_c", type: "number" },
              { label: "Max (°C)", key: "max_c", type: "number" },
              { label: "Actual (°C)", key: "actual_c", type: "number" },
              {
                label: "Comments (IP received, IP moved; fridge door left open etc)",
                key: "comments",
                type: "text",
              },
              { label: "Staff Signature", key: "staff_signature", type: "text" },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * CP03A — Biological Sample Processing Log.
 */
export function buildBiologicalSampleProcessingLogStudioReply(meta = {}) {
  const title =
    String(
      meta.title || meta.name || "Biological Sample Processing Log",
    ).trim() || "Biological Sample Processing Log";
  const ab = "Tubes A & B";
  const cd = "Tubes C & D";
  const ef = "Tubes E & F";
  const g = "Tube G";
  return {
    title,
    replace: true,
    applied: true,
    accent: "#0f766e",
    summary:
      "Rebuilt the Biological Sample Processing Log (CP03A) with study details, tube processing reference, and a repeatable processing table. Review before saving.",
    sections: [
      {
        title: "Study details",
        items: [
          { type: "short", label: "Site", key: "site", required: true, span: 6 },
          {
            type: "short",
            label: "Study Code",
            key: "study_code",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Protocol number",
            key: "protocol_number",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Sponsor name",
            key: "sponsor_name",
            required: true,
            span: 6,
          },
          {
            type: "short",
            label: "Visit",
            key: "visit",
            required: true,
            span: 6,
            placeholder: "e.g. 3, 9, 10",
          },
          {
            type: "text",
            text: "Processing instructions according to the Laboratory manual. Storage temperature applies for refrigerated / frozen samples only.",
            label: "Processing note",
            span: 12,
          },
        ],
      },
      {
        title: "Tube type reference",
        items: [
          {
            type: "datatable",
            label: "Tube types and processing instructions",
            key: "tube_type_reference",
            span: 12,
            rowMode: "fixed",
            rowHeader: "Tube",
            rowLabels: ["A & B", "C & D", "E & F", "G"],
            columns: [
              { label: "Tubes Type", key: "tubes_type", type: "text" },
              {
                label: "Processing Instructions",
                key: "processing_instructions",
                type: "text",
              },
              {
                label: "Storage Temperature",
                key: "storage_temperature",
                type: "text",
              },
              {
                label: "Shipment frequency",
                key: "shipment_frequency",
                type: "text",
              },
            ],
          },
        ],
      },
      {
        title: "Processing log",
        items: [
          {
            type: "datatable",
            label: "Sample processing entries",
            key: "sample_processing_log",
            required: true,
            span: 12,
            rowMode: "repeat",
            rows: 6,
            columns: [
              { label: "Date", key: "proc_date", type: "date" },
              { label: "Participant No.", key: "participant_no", type: "text" },
              {
                label: "Time samples obtained",
                key: "time_obtained",
                type: "time",
              },
              {
                label: "Samples processed as per laboratory manual by (Initials & date)",
                key: "processed_by",
                type: "text",
              },
              {
                label: "Comments (issues with processing/storage)",
                key: "comments",
                type: "text",
              },
              {
                label: "Time into centrifuge",
                key: "ab_time_centrifuge",
                type: "time",
                group: ab,
              },
              {
                label: "Refrigerated (Y/N)",
                key: "ab_refrigerated",
                type: "yesno",
                group: ab,
              },
              {
                label: "Spin time (mins)",
                key: "ab_spin_mins",
                type: "number",
                group: ab,
              },
              {
                label: "Speed (g)",
                key: "ab_speed_g",
                type: "text",
                group: ab,
              },
              {
                label: "Storage temp / time into freezer",
                key: "ab_storage",
                type: "text",
                group: ab,
              },
              {
                label: "Time into centrifuge",
                key: "cd_time_centrifuge",
                type: "time",
                group: cd,
              },
              {
                label: "Refrigerated (Y/N)",
                key: "cd_refrigerated",
                type: "yesno",
                group: cd,
              },
              {
                label: "Spin time (mins)",
                key: "cd_spin_mins",
                type: "number",
                group: cd,
              },
              {
                label: "Speed (g)",
                key: "cd_speed_g",
                type: "text",
                group: cd,
              },
              {
                label: "Storage temp / time into freezer",
                key: "cd_storage",
                type: "text",
                group: cd,
              },
              {
                label: "Time into centrifuge",
                key: "ef_time_centrifuge",
                type: "time",
                group: ef,
              },
              {
                label: "Refrigerated (Y/N)",
                key: "ef_refrigerated",
                type: "yesno",
                group: ef,
              },
              {
                label: "Spin time (mins)",
                key: "ef_spin_mins",
                type: "number",
                group: ef,
              },
              {
                label: "Speed (g)",
                key: "ef_speed_g",
                type: "text",
                group: ef,
              },
              {
                label: "Storage temp / time into freezer",
                key: "ef_storage",
                type: "text",
                group: ef,
              },
              {
                label: "Time into centrifuge",
                key: "g_time_centrifuge",
                type: "time",
                group: g,
              },
              {
                label: "Refrigerated (Y/N)",
                key: "g_refrigerated",
                type: "yesno",
                group: g,
              },
              {
                label: "Spin time (mins)",
                key: "g_spin_mins",
                type: "number",
                group: g,
              },
              {
                label: "Speed (g)",
                key: "g_speed_g",
                type: "text",
                group: g,
              },
              {
                label: "Storage temp / time into freezer",
                key: "g_storage",
                type: "text",
                group: g,
              },
            ],
          },
        ],
      },
    ],
  };
}

function ynCheck(label, key, span = 6) {
  return { type: "yesno", label, key, required: true, span };
}

function shortField(label, key, opts = {}) {
  return {
    type: "short",
    label,
    key,
    required: !!opts.required,
    span: opts.span ?? 6,
  };
}

/**
 * CP07B — Monthly Study Specific IMP Compliance Check.
 */
export function buildImpComplianceCheckStudioReply(meta = {}) {
  const title =
    String(
      meta.title ||
        meta.name ||
        "Monthly Study Specific IMP Compliance Check",
    ).trim() || "Monthly Study Specific IMP Compliance Check";
  return {
    title,
    replace: true,
    applied: true,
    accent: "#6b2d7b",
    summary:
      "Rebuilt the Monthly Study Specific IMP Compliance Check (CP07B) with study/IMP details, Safety Checks, Accountability Checks, and review/sign-off. Review before saving.",
    sections: [
      {
        title: "Study details",
        items: [
          shortField("Study Name", "study_name", { required: true }),
          shortField("Internal Study Code", "internal_study_code", {
            required: true,
          }),
          shortField("Protocol No", "protocol_no", { required: true }),
          shortField("Site", "site", { required: true }),
          shortField("Principal Investigator", "principal_investigator", {
            required: true,
          }),
          shortField("Site Number", "site_number", { required: true }),
          {
            type: "date",
            label: "Date of last check",
            key: "date_of_last_check",
            required: true,
            span: 6,
          },
          shortField("Responsible Person", "responsible_person", {
            required: true,
          }),
        ],
      },
      {
        title: "IMP details",
        items: [
          shortField("Name of IMP", "name_of_imp", { required: true }),
          shortField("Storage Requirements", "storage_requirements", {
            required: true,
          }),
          shortField("Location", "location", { required: true }),
          {
            type: "long",
            label:
              "Check IMP Location Log is up to date (moved since last check? include date/time of move)",
            key: "location_log_notes",
            span: 12,
          },
          shortField("Initials / Date (location log)", "location_log_initials"),
        ],
      },
      {
        title: "Safety checks",
        items: [
          ynCheck(
            "Have any IMP kits passed expiry date?",
            "kits_passed_expiry",
          ),
          shortField("Initials / Date (expiry)", "expiry_check_initials", {
            required: true,
          }),
          {
            type: "checkboxes",
            label: "If yes — ensure the following is / has been completed",
            key: "expiry_follow_up",
            span: 12,
            options: [
              "N/A",
              "Quarantine expired IMP as per SOP CP07",
              "Complete part 1 of TC07D",
              "Update IRT to register kits as expired",
              "Update accountability logs",
              "Email CRA to confirm quarantine of expired IMP",
            ],
          },
          ynCheck(
            "Has there been any quarantined stock released?",
            "quarantined_stock_released",
          ),
          shortField(
            "Initials / Date (quarantine release)",
            "quarantine_release_initials",
            { required: true },
          ),
          {
            type: "checkboxes",
            label: "If yes — ensure the following is / has been completed",
            key: "quarantine_release_follow_up",
            span: 12,
            options: [
              "N/A",
              "Print sponsor IMP release email",
              "Complete part 2 of TC07D",
              "Replace IMP in correct storage location",
              "Update IRT to register stock as available",
              "File all related paperwork in PSF",
            ],
          },
        ],
      },
      {
        title: "Accountability checks",
        items: [
          ynCheck(
            "QC Individual Subject and Master / Combined Accountability Logs",
            "qc_accountability_logs",
          ),
          shortField("Initials / Date", "qc_accountability_initials", {
            required: true,
          }),
          ynCheck(
            "Online IMP Accountability (if applicable)",
            "online_imp_accountability",
          ),
          shortField("Initials / Date", "online_accountability_initials", {
            required: true,
          }),
          ynCheck(
            "Number of kits present matches IRT report (if applicable)",
            "kits_match_irt",
          ),
          shortField("Initials / Date", "kits_match_irt_initials", {
            required: true,
          }),
          ynCheck(
            "Returned/Used IMP Reconciliation",
            "returned_used_reconciliation",
          ),
          shortField("Initials / Date", "returned_used_initials", {
            required: true,
          }),
          ynCheck(
            "QC Temperature Monitoring Logs and file copies in PSF",
            "qc_temperature_logs",
          ),
          shortField("Initials / Date", "qc_temperature_initials", {
            required: true,
          }),
          ynCheck(
            "Has there been any temperature excursions, if so, document below",
            "temperature_excursions",
          ),
          shortField("Initials / Date", "temperature_excursion_initials", {
            required: true,
          }),
          ynCheck(
            "Unused IMP returned for destruction (if applicable)",
            "unused_imp_destruction",
          ),
          shortField("Initials / Date", "unused_imp_initials", {
            required: true,
          }),
          ynCheck(
            "Non-IMP accountability and expiry check (if applicable)",
            "non_imp_accountability",
          ),
          shortField("Initials / Date", "non_imp_initials", { required: true }),
          ynCheck("Quality issue raised", "quality_issue_raised"),
          shortField("Initials / Date", "quality_issue_initials", {
            required: true,
          }),
          {
            type: "long",
            label: "Findings / notes for accountability checks",
            key: "accountability_findings",
            span: 12,
          },
        ],
      },
      {
        title: "Review and approval",
        items: [
          shortField(
            "Study Coordinator review — Sign",
            "sc_review_sign",
            { required: true },
          ),
          {
            type: "date",
            label: "Study Coordinator review — Date",
            key: "sc_review_date",
            required: true,
            span: 6,
          },
          shortField(
            "Principal Investigator Approval (or Sub-I if external) — Sign",
            "pi_approval_sign",
            { required: true },
          ),
          {
            type: "date",
            label: "Principal Investigator Approval — Date",
            key: "pi_approval_date",
            required: true,
            span: 6,
          },
          {
            type: "long",
            label: "Issues Identified and Actions Taken",
            key: "issues_and_actions",
            span: 12,
          },
          shortField("Initials / Date (issues)", "issues_initials"),
          shortField("Completed By (RN / Delegate Name)", "completed_by", {
            required: true,
          }),
          shortField("Signature", "completed_by_signature", { required: true }),
          {
            type: "date",
            label: "Date completed",
            key: "completed_date",
            required: true,
            span: 6,
          },
        ],
      },
    ],
  };
}

/**
 * Match known client-priority forms (from uploaded Word/PDF or chat create).
 * Returns a Form Studio ai-result payload, or null.
 */
export function matchKnownStudioFormTemplate(text, meta = {}) {
  const info = {
    title: meta.title || meta.name,
    name: meta.name || meta.title,
    fileName: meta.fileName,
  };
  if (looksLikeImpAccountabilityLog(text, info)) {
    return buildImpAccountabilityLogStudioReply(info);
  }
  if (looksLikeTemperatureMonitoringLog(text, info)) {
    return buildTemperatureMonitoringLogStudioReply(info);
  }
  if (looksLikeBiologicalSampleProcessingLog(text, info)) {
    return buildBiologicalSampleProcessingLogStudioReply(info);
  }
  if (looksLikeImpComplianceCheck(text, info)) {
    return buildImpComplianceCheckStudioReply(info);
  }
  return null;
}

/**
 * Build Form Studio AI reply shape for add-to-current-form from AQ Assist result.
 * @param {object} assistResult
 * @param {string} [mode]
 * @param {{ currentFields?: object[], replace?: boolean }} [options]
 */
export function formStudioReplyFromAssistResult(
  assistResult,
  mode = "add",
  options = {},
) {
  const replace =
    !!options.replace ||
    (!!assistResult?.replaceCanvas && !assistResult?.incremental);
  const fields = pickFieldsToAddOnStudio(
    assistResult,
    replace ? [] : options.currentFields || [],
  );
  const summary =
    String(assistResult?.summary || "").trim() ||
    "AQ Assist updated the form.";
  const title =
    String(assistResult?.name || assistResult?.title || "").trim() ||
    "Untitled form";

  if (!fields.length) {
    return { sections: [], summary, applied: false, replace: false };
  }

  const spec = mapEsourceFieldsToFormStudioSpec(fields, {
    title,
    summary,
    description: summary,
  });

  return {
    title: spec.title,
    sections: spec.sections,
    summary,
    applied: true,
    replace,
  };
}
